import {
  type ISeriesPrimitive,
  type IPrimitivePaneView,
  type IPrimitivePaneRenderer,
  type SeriesAttachedParameter,
  type IChartApiBase,
  type ISeriesApi,
  type SeriesType,
  type Time,
} from "lightweight-charts"

interface CanvasTarget {
  useBitmapCoordinateSpace<T>(
    f: (scope: {
      context: CanvasRenderingContext2D
      horizontalPixelRatio: number
      verticalPixelRatio: number
      bitmapSize: { width: number; height: number }
    }) => T,
  ): T
}

export interface OverlayParams {
  bestBid: number | null
  bestAsk: number | null
  lastPrice: number | null
}

class OverlayRenderer implements IPrimitivePaneRenderer {
  constructor(
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: OverlayParams,
  ) {}

  draw(target: CanvasTarget): void {
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr, bitmapSize }) => {
      const series = this._series
      const { bestBid, bestAsk, lastPrice } = this._p

      // Spread band fill between best bid and best ask
      if (bestBid !== null && bestAsk !== null && bestAsk > bestBid) {
        const yAsk = series.priceToCoordinate(bestAsk)
        const yBid = series.priceToCoordinate(bestBid)
        if (yAsk !== null && yBid !== null) {
          const yAskPx = Math.round(Math.min(yAsk, yBid) * vpr)
          const yBidPx = Math.round(Math.max(yAsk, yBid) * vpr)
          ctx.fillStyle = "rgba(255,220,80,0.05)"
          ctx.fillRect(0, yAskPx, bitmapSize.width, yBidPx - yAskPx)
        }
      }

      ctx.lineWidth = hpr

      // Best bid dashed line
      if (bestBid !== null) {
        const y = series.priceToCoordinate(bestBid)
        if (y !== null) {
          ctx.setLineDash([4 * hpr, 4 * hpr])
          ctx.strokeStyle = "rgba(0,200,80,0.65)"
          ctx.beginPath()
          ctx.moveTo(0, Math.round(y * vpr))
          ctx.lineTo(bitmapSize.width, Math.round(y * vpr))
          ctx.stroke()
        }
      }

      // Best ask dashed line
      if (bestAsk !== null) {
        const y = series.priceToCoordinate(bestAsk)
        if (y !== null) {
          ctx.setLineDash([4 * hpr, 4 * hpr])
          ctx.strokeStyle = "rgba(220,60,60,0.65)"
          ctx.beginPath()
          ctx.moveTo(0, Math.round(y * vpr))
          ctx.lineTo(bitmapSize.width, Math.round(y * vpr))
          ctx.stroke()
        }
      }

      // Last fill price — solid horizontal reference line
      if (lastPrice !== null) {
        const y = series.priceToCoordinate(lastPrice)
        if (y !== null) {
          ctx.setLineDash([])
          ctx.lineWidth = Math.round(1.5 * hpr)
          ctx.strokeStyle = "rgba(220,230,255,0.55)"
          ctx.beginPath()
          ctx.moveTo(0, Math.round(y * vpr))
          ctx.lineTo(bitmapSize.width, Math.round(y * vpr))
          ctx.stroke()
        }
      }

      ctx.setLineDash([])
    })
  }
}

class OverlayPaneView implements IPrimitivePaneView {
  constructor(
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: OverlayParams,
  ) {}

  renderer(): IPrimitivePaneRenderer {
    return new OverlayRenderer(this._series, this._p)
  }
}

export class OverlayPrimitive implements ISeriesPrimitive<Time> {
  private _p: OverlayParams
  private _chart: IChartApiBase<Time> | null = null
  private _series: ISeriesApi<SeriesType, Time> | null = null
  private _requestUpdate: (() => void) | null = null

  constructor(p: OverlayParams) {
    this._p = p
  }

  attached({ chart, series, requestUpdate }: SeriesAttachedParameter<Time>): void {
    this._chart = chart
    this._series = series
    this._requestUpdate = requestUpdate
  }

  detached(): void {
    this._chart = null
    this._series = null
    this._requestUpdate = null
  }

  updateParams(params: Partial<OverlayParams>): void {
    this._p = { ...this._p, ...params }
    this._requestUpdate?.()
  }

  paneViews(): readonly IPrimitivePaneView[] {
    if (!this._series) return []
    return [new OverlayPaneView(this._series, this._p)]
  }
}
